import * as React from 'react';
import {useTranslation} from 'react-i18next';
import {AllPayrollPeriod, getAllPayrollPeriods} from '../services/payrollService';
import {useAuth} from '../context/AuthContext';
import {usePeriod} from '../context/PeriodContext';
import {API_BASE_URL} from '../config/apiConfig';

function stripDiacritics(s: string): string {
    return s.normalize('NFD').replace(/[̀-ͯ]/g, '');
}

const BASIS_VALUE_PATTERN = /^(bruto|neto|gross|net)$/i;
const BASIS_WORD_PATTERN = /\b(bruto|neto|gross|net)\b/i;

// Some environments have been observed to bake the salary basis straight into an identity
// column's value (e.g. "Arta Krasniqi Net") instead of returning it as its own column — strip
// it defensively wherever it could leak into the employee's display name.
function stripBasisWord(value: string): string {
    return value.replace(new RegExp(BASIS_WORD_PATTERN, 'gi'), ' ').replace(/\s+/g, ' ').trim();
}

// ─── Sorting helpers ─────────────────────────────────────────────────────────
export type Order = 'asc' | 'desc';

export interface MonthlyChangeItem {
    id: string;
    type: string;
    description: string;
    taxBasis: string;
}

export interface ColumnDefinition {
    id: string;
    name: string;
    key: string | null;
    type?: string | null;
    taxBasis?: string | null;
    editable: boolean;
}

export interface CellData {
    id: string;
    name: string;
    key: string | null;
    editable: boolean;

    [key: string]: any;
}

export interface RowData {
    id: string;
    cells: CellData[];
}

export interface MonthlyChangeTableData {
    totalRecords: number;
    columns: ColumnDefinition[];
    rows: RowData[];
}

// ─── Treatment helpers (type + taxBasis → label/color for badges) ────────────
export type ItemTreatmentKey = 'allowanceGross' | 'allowanceNet' | 'deductionGross' | 'deductionNet';

interface TreatmentSource {
    type?: string | null;
    taxBasis?: string | null;
    description?: string | null;
    name?: string | null;
}

// Item names known to be allowances regardless of what the backend's type/taxBasis says for
// them — the monthly-change table endpoint often leaves type/taxBasis null (see
// getColumnTreatmentSource below), which falls through to "deduction" by default.
const ALLOWANCE_NAME_OVERRIDES = new Set(['bonus']);

export function treatmentKeyFor(item: TreatmentSource): ItemTreatmentKey {
    // Case/whitespace-insensitive: the type/taxBasis strings come straight from the backend
    // and aren't guaranteed to be normalized (e.g. "Allowance" vs "allowance").
    const name = (item.description ?? item.name ?? '').trim().toLowerCase();
    const isAllowance = (item.type ?? '').trim().toUpperCase() === 'ALLOWANCE' || ALLOWANCE_NAME_OVERRIDES.has(name);
    const isGross = (item.taxBasis ?? '').trim().toUpperCase() === 'GROSS';
    if (isAllowance && isGross) return 'allowanceGross';
    if (isAllowance) return 'allowanceNet';
    if (isGross) return 'deductionGross';
    return 'deductionNet';
}

export function getItemTreatmentLabelKey(item: TreatmentSource): string {
    return `payroll.monthlyChange.treatment.${treatmentKeyFor(item)}`;
}

const TREATMENT_COLORS: Record<ItemTreatmentKey, { bg: string; color: string }> = {
    allowanceGross: {bg: '#E3F2FD', color: '#1565C0'},
    allowanceNet: {bg: '#E0F2F1', color: '#00695C'},
    deductionGross: {bg: '#FFF3E0', color: '#E65100'},
    deductionNet: {bg: '#FCE4EC', color: '#AD1457'},
};

export function getItemTreatmentColors(item: TreatmentSource): { bg: string; color: string } {
    return TREATMENT_COLORS[treatmentKeyFor(item)];
}

export function useMonthlyChange() {
    const {t} = useTranslation();

    const {token} = useAuth();
    // Period + branch are both read from the global PeriodContext — the top toolbar's
    // selectors are the single source of truth, mirroring usePayrollRun/useReports.
    const {selectedPeriod, selectedPeriodValue, setPeriodAndValue, dashboardBranch} = usePeriod();
    const selectedBranch = dashboardBranch;
    const [allPeriods, setAllPeriods] = React.useState<AllPayrollPeriod[]>([]);
    const [periodError, setPeriodError] = React.useState<string>('');
    const [description, setDescription] = React.useState<string>('');
    const [type, setType] = React.useState<string>('');
    const [calcType, setCalcType] = React.useState<string>('');
    const [showSuccessMessage, setShowSuccessMessage] = React.useState<boolean>(false);
    const [successMessageText, setSuccessMessageText] = React.useState<string>('');
    const [monthlyChangeItems, setMonthlyChangeItems] = React.useState<MonthlyChangeItem[]>([]);
    const [isLoading, setIsLoading] = React.useState<boolean>(false);
    const [currentPage, setCurrentPage] = React.useState<number>(1);
    const itemsPerPage = 5;
    const [isPeriodLocked, setIsPeriodLocked] = React.useState<boolean>(
        selectedPeriodValue?.toUpperCase().includes('LOCKED') ?? false,
    );
    const isPeriodOpen = selectedPeriodValue?.toUpperCase().includes('OPEN') ?? false;
    const [deleteConfirmOpen, setDeleteConfirmOpen] = React.useState<boolean>(false);
    const [deleteItemId, setDeleteItemId] = React.useState<string | null>(null);

    // State for the monthly change data table
    const [monthlyChangeTableData, setMonthlyChangeTableData] = React.useState<MonthlyChangeTableData | null>(null);
    const [isTableDataLoading, setIsTableDataLoading] = React.useState<boolean>(false);
    const [tableDataCurrentPage, setTableDataCurrentPage] = React.useState<number>(1);
    const tableDataItemsPerPage = 5;

    // State for tracking edited cells
    const [editedCells, setEditedCells] = React.useState<{ [key: string]: string }>({});
    const [activeCellId, setActiveCellId] = React.useState<string | null>(null);

    // Helper variable to check if form controls should be disabled (no selection or locked)
    const isFormDisabled = !selectedPeriod || isPeriodLocked;

    // ─── Employee-changes table: search/filter/column-visibility (client-side) ──────────────────
    const [employeeSearchQuery, setEmployeeSearchQuery] = React.useState<string>('');
    const [onlyWithChanges, setOnlyWithChanges] = React.useState<boolean>(false);
    const [visibleColumnIds, setVisibleColumnIds] = React.useState<Set<string> | null>(null); // null = all visible
    const [itemsDialogOpen, setItemsDialogOpen] = React.useState<boolean>(false);
    const [columnsDialogOpen, setColumnsDialogOpen] = React.useState<boolean>(false);

    // ─── "Artikujt e pagës" dialog: search/filter over configured items ─────────────────────────
    const [itemsSearchQuery, setItemsSearchQuery] = React.useState<string>('');
    const [itemsTypeFilter, setItemsTypeFilter] = React.useState<'all' | 'Allowance' | 'Deduction'>('all');
    const [itemsBasisFilter, setItemsBasisFilter] = React.useState<'all' | 'NET' | 'GROSS'>('all');
    const [addItemFormOpen, setAddItemFormOpen] = React.useState<boolean>(false);

    const pinnedColumns = React.useMemo(
        () => (monthlyChangeTableData?.columns ?? []).filter((c) => c.name !== 'contractId' && !c.editable),
        [monthlyChangeTableData],
    );
    const editableColumns = React.useMemo(
        () => (monthlyChangeTableData?.columns ?? []).filter((c) => c.name !== 'contractId' && c.editable),
        [monthlyChangeTableData],
    );
    const visibleEditableColumns = React.useMemo(
        () => (visibleColumnIds === null ? editableColumns : editableColumns.filter((c) => visibleColumnIds.has(c.id))),
        [editableColumns, visibleColumnIds],
    );

    const getCellDisplayValue = React.useCallback((row: RowData, column: ColumnDefinition): string => {
        if (!monthlyChangeTableData) return '';
        const idx = monthlyChangeTableData.columns.findIndex((c) => c.id === column.id);
        const cellData = idx >= 0 ? row.cells[idx] : undefined;
        const cellId = `${row.id}-${column.id}`;
        return editedCells[cellId] !== undefined ? editedCells[cellId] : (cellData?.name ?? '');
    }, [monthlyChangeTableData, editedCells]);

    // The backend's non-editable ("pinned") columns mix employee identity with contract fields
    // (workload, base amount, salary basis) — and their labels alone can't be trusted to tell
    // "base amount" apart from "salary basis" (both use the Albanian word for "base", "bazë").
    // Classify each pinned column by its actual cell values instead: a closed Bruto/Neto/Gross/Net
    // enum is the salary basis, a purely-numeric column is the base amount, and "workload" is
    // recognized by name and dropped entirely per product decision (it doesn't belong here).
    const pinnedColumnRoles = React.useMemo(() => {
        const roles = new Map<string, 'workload' | 'basis' | 'amount' | 'name'>();
        if (!monthlyChangeTableData) return roles;
        pinnedColumns.forEach((column) => {
            const label = stripDiacritics(column.name).toLowerCase();
            if (/ngarkes|workload/.test(label)) {
                roles.set(column.id, 'workload');
                return;
            }
            const idx = monthlyChangeTableData.columns.findIndex((c) => c.id === column.id);
            const values = idx >= 0
                ? monthlyChangeTableData.rows.map((r) => r.cells[idx]?.name).filter((v): v is string => !!v && v !== '-')
                : [];
            if (values.length > 0 && values.every((v) => BASIS_VALUE_PATTERN.test(v.trim()))) {
                roles.set(column.id, 'basis');
            } else if (values.length > 0 && values.every((v) => !Number.isNaN(Number.parseFloat(v.replace(',', '.'))))) {
                roles.set(column.id, 'amount');
            } else {
                roles.set(column.id, 'name');
            }
        });
        return roles;
    }, [pinnedColumns, monthlyChangeTableData]);

    // The employee cell shows ONLY first name + last name. The backend column names are the raw
    // ContractResponse field names, so pick those two explicitly; any other non-numeric pinned
    // column (id, contract number, ...) must never leak into the name. Fall back to the
    // value-based guess only when neither name column is present.
    const employeeNameColumns = React.useMemo(() => {
        const byName = (name: string) => pinnedColumns.find((c) => c.name.toLowerCase() === name.toLowerCase());
        const explicit = [byName('firstName'), byName('lastName')].filter((c): c is ColumnDefinition => !!c);
        return explicit.length > 0
            ? explicit
            : pinnedColumns.filter((c) => pinnedColumnRoles.get(c.id) === 'name');
    }, [pinnedColumns, pinnedColumnRoles]);
    const basePayColumn = React.useMemo(
        () => pinnedColumns.find((c) => pinnedColumnRoles.get(c.id) === 'amount') ?? null,
        [pinnedColumns, pinnedColumnRoles],
    );
    const salaryBasisColumn = React.useMemo(
        () => pinnedColumns.find((c) => pinnedColumnRoles.get(c.id) === 'basis') ?? null,
        [pinnedColumns, pinnedColumnRoles],
    );

    const getEmployeeName = React.useCallback((row: RowData): string => {
        return employeeNameColumns
            .map((col) => stripBasisWord(getCellDisplayValue(row, col)))
            .filter(Boolean)
            .join(' ')
            .trim();
    }, [employeeNameColumns, getCellDisplayValue]);

    const getEmployeeInitials = React.useCallback((row: RowData): string => {
        if (employeeNameColumns.length >= 2) {
            const first = stripBasisWord(getCellDisplayValue(row, employeeNameColumns[0])).trim().charAt(0);
            const last = stripBasisWord(getCellDisplayValue(row, employeeNameColumns[employeeNameColumns.length - 1])).trim().charAt(0);
            return `${first}${last}`.toUpperCase() || '?';
        }
        const parts = getEmployeeName(row).split(/\s+/).filter(Boolean);
        return (parts[0]?.charAt(0) ?? '') + (parts[1]?.charAt(0) ?? '') || (parts[0]?.charAt(0)?.toUpperCase() ?? '?');
    }, [employeeNameColumns, getCellDisplayValue, getEmployeeName]);

    // "900 Gross" — the base amount combined with its salary basis, matching how base pay is
    // shown on the Payroll Period page. If the amount value already carries the basis word
    // itself (some responses bake it in), don't double it up with a separately-found column.
    const getBasePayDisplay = React.useCallback((row: RowData): string => {
        if (!basePayColumn) return '';
        const amount = getCellDisplayValue(row, basePayColumn);
        if (!amount) return '';
        if (BASIS_WORD_PATTERN.test(amount)) return amount;
        const basis = salaryBasisColumn ? getCellDisplayValue(row, salaryBasisColumn) : '';
        return basis ? `${amount} ${basis}` : amount;
    }, [basePayColumn, salaryBasisColumn, getCellDisplayValue]);

    // Reset the column-visibility filter whenever the set of configured items changes
    // (e.g. an item is added/removed) so it never references a stale column id.
    const editableColumnIdsKey = editableColumns.map((c) => c.id).join(',');
    React.useEffect(() => {
        setVisibleColumnIds(null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editableColumnIdsKey]);

    // ─── Sorting state ────────────────────────────────────────────────────────
    const [order, setOrder] = React.useState<Order>('asc');
    const [orderBy, setOrderBy] = React.useState<string | null>(null);

    const handleRequestSort = (columnId: string) => {
        const isAsc = orderBy === columnId && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(columnId);
    };

    const sortedRows = React.useMemo(() => {
        if (!monthlyChangeTableData) return [];
        const rows = [...monthlyChangeTableData.rows];
        if (!orderBy) return rows;
        const columnIndex = monthlyChangeTableData.columns.findIndex((col) => col.id === orderBy);
        if (columnIndex === -1) return rows;
        return rows.sort((a, b) => {
            const aVal = a.cells[columnIndex]?.name ?? '';
            const bVal = b.cells[columnIndex]?.name ?? '';
            const aNum = Number.parseFloat(aVal);
            const bNum = Number.parseFloat(bVal);
            if (!Number.isNaN(aNum) && !Number.isNaN(bNum)) {
                return order === 'asc' ? aNum - bNum : bNum - aNum;
            }
            if (bVal < aVal) return order === 'asc' ? -1 : 1;
            if (bVal > aVal) return order === 'asc' ? 1 : -1;
            return 0;
        });
    }, [monthlyChangeTableData, order, orderBy]);

    // Rows filtered by the employee search box + "only with changes" toggle (client-side).
    const filteredTableRows = React.useMemo(() => {
        return sortedRows.filter((row) => {
            if (employeeSearchQuery.trim()) {
                const name = getEmployeeName(row).toLowerCase();
                if (!name.includes(employeeSearchQuery.trim().toLowerCase())) return false;
            }
            if (onlyWithChanges) {
                const hasChange = editableColumns.some((col) => {
                    const val = getCellDisplayValue(row, col);
                    const num = Number.parseFloat(val);
                    return val && val !== '-' && !Number.isNaN(num) && num !== 0;
                });
                if (!hasChange) return false;
            }
            return true;
        });
    }, [sortedRows, editableColumns, employeeSearchQuery, onlyWithChanges, getCellDisplayValue, getEmployeeName]);

    // Per-column totals (over the currently filtered rows) + grouped totals by treatment.
    const columnTotals = React.useMemo(() => {
        const totals: Record<string, number> = {};
        editableColumns.forEach((col) => {
            totals[col.id] = filteredTableRows.reduce((sum, row) => {
                const num = Number.parseFloat(getCellDisplayValue(row, col));
                return sum + (Number.isNaN(num) ? 0 : num);
            }, 0);
        });
        return totals;
    }, [editableColumns, filteredTableRows, getCellDisplayValue]);

    const pendingEditsCount = React.useMemo(
        () => Object.values(editedCells).filter((value) => value !== undefined && value !== '' && value !== '-').length,
        [editedCells],
    );
    const hasActiveEdits = pendingEditsCount > 0;

    // ─── "Artikujt e pagës" dialog: item counts + filtering ──────────────────────────────────────
    const itemCounts = React.useMemo(() => ({
        all: monthlyChangeItems.length,
        allowance: monthlyChangeItems.filter((i) => i.type === 'Allowance').length,
        deduction: monthlyChangeItems.filter((i) => i.type === 'Deduction').length,
    }), [monthlyChangeItems]);

    const filteredMonthlyChangeItems = React.useMemo(() => {
        return monthlyChangeItems.filter((item) => {
            if (itemsTypeFilter !== 'all' && (item.type ?? '').trim().toUpperCase() !== itemsTypeFilter.toUpperCase()) return false;
            if (itemsBasisFilter !== 'all' && (item.taxBasis ?? '').trim().toUpperCase() !== itemsBasisFilter.toUpperCase()) return false;
            if (itemsSearchQuery.trim() && !item.description.toLowerCase().includes(itemsSearchQuery.trim().toLowerCase())) return false;
            return true;
        });
    }, [monthlyChangeItems, itemsTypeFilter, itemsBasisFilter, itemsSearchQuery]);

    // The monthly-change TABLE's own column definitions don't reliably carry type/taxBasis
    // (that endpoint often leaves them null); the configured items list does. Column ids and
    // item ids come from different endpoints and don't reliably line up, but a column's header
    // is generated from the item's own name — so match on that instead (case/whitespace-insensitive).
    const itemsByName = React.useMemo(() => {
        const map: Record<string, MonthlyChangeItem> = {};
        monthlyChangeItems.forEach((item) => {
            map[item.description.trim().toLowerCase()] = item;
        });
        return map;
    }, [monthlyChangeItems]);

    // Name matching isn't perfectly reliable either (formatting can differ between the two
    // endpoints). When both lists are the same length they represent the same underlying set of
    // configured items queried in the same order, so positional matching is a solid fallback.
    const itemByColumnPosition = React.useMemo(() => {
        const map: Record<string, MonthlyChangeItem> = {};
        if (editableColumns.length === monthlyChangeItems.length) {
            editableColumns.forEach((column, index) => {
                map[column.id] = monthlyChangeItems[index];
            });
        }
        return map;
    }, [editableColumns, monthlyChangeItems]);

    const getColumnTreatmentSource = React.useCallback(
        (column: ColumnDefinition): TreatmentSource =>
            itemsByName[column.name.trim().toLowerCase()] ?? itemByColumnPosition[column.id] ?? column,
        [itemsByName, itemByColumnPosition],
    );

    // Grouped totals by treatment (shown as the mobile stat row) — routed through the same
    // getColumnTreatmentSource/treatmentKeyFor logic as the per-column chips, so a column like
    // "Bonus" totals into the same bucket its chip displays.
    const categoryTotals = React.useMemo(() => {
        const totals = {allowanceGross: 0, allowanceNet: 0, deductionGross: 0, deductionNet: 0};
        editableColumns.forEach((col) => {
            const amount = columnTotals[col.id] ?? 0;
            totals[treatmentKeyFor(getColumnTreatmentSource(col))] += amount;
        });
        return totals;
    }, [editableColumns, columnTotals, getColumnTreatmentSource]);

    const handleToggleColumnVisibility = (columnId: string) => {
        setVisibleColumnIds((prev) => {
            const current = prev ?? new Set(editableColumns.map((c) => c.id));
            const next = new Set(current);
            if (next.has(columnId)) next.delete(columnId); else next.add(columnId);
            return next;
        });
    };
    const handleShowAllColumns = () => setVisibleColumnIds(null);

    const fetchMonthlyChangeTableData = async (periodKey: string) => {
        if (!token) {
            console.error('No authentication token available');
            return;
        }

        setIsTableDataLoading(true);
        try {
            const response = await fetch(`${API_BASE_URL}/api/payroll/monthlyChange?periodKey=${periodKey}&branch=${selectedBranch}`,
                {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                    }
                }
            );

            if (!response.ok) {
                throw new Error(`Failed to fetch monthly change table data: ${response.statusText}`);
            }

            const data: MonthlyChangeTableData = await response.json();
            setMonthlyChangeTableData(data);
            setTableDataCurrentPage(1); // Reset to first page when data is fetched
        } catch (error) {
            console.error('Error fetching monthly change table data:', error);
        } finally {
            setIsTableDataLoading(false);
        }
    };

    const fetchMonthlyChangeItems = async (periodKey: string) => {
        if (!token) {
            console.error('No authentication token available');
            return;
        }

        setIsLoading(true);
        try {
            const response = await fetch(`${API_BASE_URL}/api/payroll/monthly/getColumns?periodKey=${periodKey}`,
                {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                    }
                }
            );

            if (!response.ok) {
                throw new Error(`Failed to fetch monthly change items: ${response.statusText}`);
            }

            const data = await response.json();
            setMonthlyChangeItems(data.addedColumns || []);
            setCurrentPage(1); // Reset to first page when data is fetched
        } catch (error) {
            console.error('Error fetching monthly change items:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleAddMonthlyChange = async () => {
        if (!selectedPeriod) {
            setPeriodError(t('payroll.monthlyChange.error.selectPeriod'));
            return;
        }

        if (!token) {
            console.error('No authentication token available');
            return;
        }

        try {
            const response = await fetch(`${API_BASE_URL}/api/payroll/monthly/addColumn?periodKey=${selectedPeriod}`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                    },
                    body: JSON.stringify({
                        name: description,
                        colKey: description.toLowerCase().replace(/\s+/g, '_'),
                        itemType: type,
                        taxBasis: calcType
                    })
                }
            );

            if (!response.ok) {
                throw new Error(`Failed to add monthly change: ${response.statusText}`);
            }

            // Clear form on success
            setDescription('');
            setType('');
            setCalcType('');
            setSuccessMessageText(t('payroll.monthlyChange.success.columnAdded'));
            setShowSuccessMessage(true);

            // Clear success message after 3 seconds
            setTimeout(() => setShowSuccessMessage(false), 3000);

            // Refresh both tables
            await fetchMonthlyChangeItems(selectedPeriod);
            await fetchMonthlyChangeTableData(selectedPeriod);
        } catch (error) {
            console.error('Error adding monthly change:', error);
        }
    };

    const handlePeriodChangeForMonthlyChange = async (event: any) => {
        const selectedValue = JSON.parse(event.target.value);
        const selectedPeriodKey = selectedValue.key;
        const selectedPeriodVal = selectedValue.value;

        console.log('Selected Period Key:', selectedPeriodKey);
        console.log('Selected Period Value:', selectedPeriodVal);

        setPeriodAndValue(selectedPeriodKey, selectedPeriodVal);
        setPeriodError(''); // Clear error when period is selected

        // Check if period is locked
        const isLocked = selectedPeriodVal && selectedPeriodVal.toUpperCase().includes('LOCKED');
        setIsPeriodLocked(isLocked);

        if (!token) {
            console.error('No authentication token available');
            return;
        }

        // Fetch monthly change items for the selected period
        await fetchMonthlyChangeItems(selectedPeriodKey);

        // Fetch monthly change table data for the selected period
        await fetchMonthlyChangeTableData(selectedPeriodKey);
    };

    const handleDeleteMonthlyChange = async (itemId: string) => {
        if (!selectedPeriod || !token) {
            console.error('Missing period or token');
            return;
        }

        try {
            const response = await fetch(`${API_BASE_URL}/api/payroll/monthly/deleteColumn?periodKey=${selectedPeriod}&columnId=${itemId}`,
                {
                    method: 'DELETE',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                    }
                }
            );

            if (!response.ok) {
                throw new Error(`Failed to delete monthly change: ${response.statusText}`);
            }

            // Refresh the table
            await fetchMonthlyChangeItems(selectedPeriod);
        } catch (error) {
            console.error('Error deleting monthly change:', error);
        }
    };

    const handleCellActivate = (cellId: string) => {
        setActiveCellId(cellId);
    };

    const handleCellValueChange = (cellId: string, value: string) => {
        // Validate: allow only numerical values with at most 2 decimal places
        // Allow empty string, numbers, and numbers with up to 2 decimal places
        const regex = /^(\d+\.?\d{0,2})?$/;
        if (regex.test(value) || value === '') {
            setEditedCells({...editedCells, [cellId]: value});
        }
    };

    const handleAddEdits = async () => {
        if (!selectedPeriod || !token || !monthlyChangeTableData) {
            console.error('Missing period, token, or table data');
            return;
        }

        // Build the edits array from editedCells - only include cells with valid numeric values
        const edits: Array<{
            cellId: string;
            columnId: string;
            rowId: string;
            columnName: string;
            colKey: string;
            amount: number;
        }> = [];

        // Iterate through all rows and columns to find edited cells
        monthlyChangeTableData.rows.forEach((row) => {
            monthlyChangeTableData.columns.forEach((column, columnIndex) => {
                const compositeKey = `${row.id}-${column.id}`;
                const value = editedCells[compositeKey];

                // Skip if no edit for this cell
                if (value === undefined) {
                    return;
                }

                // Skip empty values or '-'
                if (!value || value === '-' || value.trim() === '') {
                    return;
                }

                // Parse the numeric value
                const numericValue = parseFloat(value);
                if (isNaN(numericValue)) {
                    return;
                }

                // Get the cell data
                const cellData = row.cells[columnIndex];
                if (!cellData) {
                    return;
                }

                edits.push({
                    cellId: cellData.id,
                    columnId: column.id,
                    rowId: row.id,
                    columnName: column.name,
                    colKey: column.key ?? column.name?.toLowerCase() ?? '',
                    amount: numericValue
                });
            });
        });

        if (edits.length === 0) {
            console.log('No valid edits to save');
            return;
        }

        try {
            const response = await fetch(`${API_BASE_URL}/api/payroll/monthlyChange/add`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    periodKey: selectedPeriod,
                    edits
                })
            });

            if (!response.ok) {
                throw new Error(`Failed to save edits: ${response.statusText}`);
            }

            const data = await response.json();

            if (data.success) {
                setEditedCells({});
                setActiveCellId(null);
                setSuccessMessageText(t('payroll.monthlyChange.success.editsSaved'));
                setShowSuccessMessage(true);
                setTimeout(() => setShowSuccessMessage(false), 3000);

                // Refresh the table data
                await fetchMonthlyChangeTableData(selectedPeriod);
            }
        } catch (error) {
            console.error('Error saving edits:', error);
        }
    };

    const handleCancelEdits = () => {
        setEditedCells({});
        setActiveCellId(null);
    };

    React.useEffect(() => {
        const fetchAllPeriods = async () => {
            if (!token) {
                console.error('No authentication token available');
                return;
            }

            try {
                const periodsResponse = await getAllPayrollPeriods(token);
                setAllPeriods(periodsResponse);
            } catch (error) {
                console.error('Failed to fetch all payroll periods:', error);
                setAllPeriods([]);
            }
        };

        fetchAllPeriods();
    }, [token]);

    // Auto-fetch whenever the period or branch changes (restored from session, or changed via
    // the top toolbar's PeriodBranchToolbarSelector) — not just on initial mount.
    React.useEffect(() => {
        if (token && selectedPeriod) {
            fetchMonthlyChangeItems(selectedPeriod);
            fetchMonthlyChangeTableData(selectedPeriod);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token, selectedPeriod, selectedBranch]);

    return {
        selectedPeriod,
        selectedPeriodValue,
        allPeriods,
        periodError,
        description,
        setDescription,
        type,
        setType,
        calcType,
        setCalcType,
        showSuccessMessage,
        setShowSuccessMessage,
        successMessageText,
        monthlyChangeItems,
        isLoading,
        currentPage,
        setCurrentPage,
        itemsPerPage,
        isPeriodLocked,
        isPeriodOpen,
        deleteConfirmOpen,
        setDeleteConfirmOpen,
        deleteItemId,
        setDeleteItemId,

        monthlyChangeTableData,
        isTableDataLoading,
        tableDataCurrentPage,
        setTableDataCurrentPage,
        tableDataItemsPerPage,

        editedCells,
        activeCellId,

        isFormDisabled,
        hasActiveEdits,
        pendingEditsCount,

        order,
        orderBy,
        handleRequestSort,
        sortedRows,

        // Employee-changes table: search/filter/columns
        employeeSearchQuery,
        setEmployeeSearchQuery,
        onlyWithChanges,
        setOnlyWithChanges,
        pinnedColumns,
        employeeNameColumns,
        basePayColumn,
        salaryBasisColumn,
        getEmployeeName,
        getEmployeeInitials,
        getBasePayDisplay,
        editableColumns,
        visibleEditableColumns,
        visibleColumnIds,
        filteredTableRows,
        columnTotals,
        categoryTotals,
        getCellDisplayValue,
        handleToggleColumnVisibility,
        handleShowAllColumns,
        columnsDialogOpen,
        setColumnsDialogOpen,

        // "Artikujt e pagës" dialog
        itemsDialogOpen,
        setItemsDialogOpen,
        itemsSearchQuery,
        setItemsSearchQuery,
        itemsTypeFilter,
        setItemsTypeFilter,
        itemsBasisFilter,
        setItemsBasisFilter,
        itemCounts,
        filteredMonthlyChangeItems,
        getColumnTreatmentSource,
        addItemFormOpen,
        setAddItemFormOpen,

        handleAddMonthlyChange,
        handlePeriodChangeForMonthlyChange,
        handleDeleteMonthlyChange,
        handleCellActivate,
        handleCellValueChange,
        handleAddEdits,
        handleCancelEdits,
    };
}
